import { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, RefreshCw, Search, Eye, X, Check, XCircle,
  MapPin, Star, BedDouble, CalendarCheck, Euro, Loader2, Inbox, Power
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

const ISLAND_LABELS = { Ngazidja: "Ngazidja", Anjouan: "Anjouan", Moheli: "Mohéli" };
const TYPE_LABELS = {
  hotel: "Hôtel",
  chambre_hote: "Chambre d'hôte",
  appartement_studio: "Appart / Studio",
};

function StatusPill({ status }) {
  const map = {
    actif:       { label: "Actif",       cls: "bg-emerald-50 text-emerald-700" },
    inactif:     { label: "Inactif",     cls: "bg-gray-100 text-gray-500" },
    non_verifie: { label: "Non vérifié", cls: "bg-orange-50 text-orange-600" },
  };
  const s = map[status] || map.inactif;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

// Pastille active/inactive simple (pour l'en-tête du drawer, basée sur le booléen hotel.active)
function ActivePill({ active }) {
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
    }`}>
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function QualityBar({ score }) {
  const color = score >= 70 ? "bg-[var(--k-green)]" : score >= 40 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-7 text-right">{score}</span>
    </div>
  );
}

function RequestStatusPill({ status }) {
  const map = {
    pending:   { label: "Reçue",   cls: "bg-blue-50 text-blue-600" },
    validated: { label: "Validée", cls: "bg-emerald-50 text-emerald-700" },
    rejected:  { label: "Rejetée", cls: "bg-red-50 text-red-500" },
  };
  const s = map[status] || map.pending;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

// ── Drawer détail hôtel ──
function HotelDrawer({ hotelId, onClose, onToggled }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visionLoading, setVisionLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/hotels/${hotelId}`, { withCredentials: true })
      .then(r => setDetail(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [hotelId]);

  const handleVision = async () => {
    setVisionLoading(true);
    try {
      await axios.post(`${API}/hotels/${hotelId}/vision`, {}, { withCredentials: true });
      window.location.href = `/superadmin/vision/${hotelId}`;
    } catch {
      alert("Impossible d'activer le Mode Vision.");
      setVisionLoading(false);
    }
  };

  // 🆕 Activer / désactiver l'hôtel (action super admin directe, réversible)
  const handleToggleActive = async () => {
    setToggleLoading(true);
    try {
      const r = await axios.patch(`${API}/hotels/${hotelId}/toggle-active`, {}, { withCredentials: true });
      setDetail(d => d ? { ...d, hotel: { ...d.hotel, active: r.data.active } } : d); // maj locale immédiate
      onToggled?.(); // rafraîchit la liste (le statut dérivé peut changer)
    } catch {
      alert("Impossible de modifier le statut de l'hôtel.");
    } finally {
      setToggleLoading(false);
    }
  };

  const h = detail?.hotel;
  const s = detail?.stats;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-screen w-[440px] max-w-[calc(100vw-24px)] bg-white border-l border-[#e5e7eb] z-50 overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb] sticky top-0 bg-white">
          <p className="text-sm font-medium text-gray-900">Fiche hôtel</p>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 transition">
            <X size={15} />
          </button>
        </div>

        {loading ? (
          <div className="p-5"><p className="text-xs text-gray-400">Chargement...</p></div>
        ) : !h ? (
          <div className="p-5"><p className="text-xs text-red-500">Hôtel introuvable.</p></div>
        ) : (
          <div className="p-5 flex flex-col gap-5">

            {/* Photo + identité */}
            <div>
              <div className="w-full h-40 rounded-xl bg-gray-100 overflow-hidden mb-3">
                {h.photos?.[0]
                  ? <img src={h.photos[0]} alt={h.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center"><Building2 className="text-gray-300" size={28} /></div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-semibold text-gray-900">{h.name}</p>
                <ActivePill active={h.active} /> {/* 🆕 repère visuel du statut */}
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                <MapPin size={12} />
                {h.city}, {ISLAND_LABELS[h.island] || h.island} · {TYPE_LABELS[h.type] || h.type}
              </div>
              {h.rating != null && (
                <div className="flex items-center gap-1 mt-1 text-xs text-amber-500">
                  <Star size={12} fill="currentColor" /> {h.rating}/5
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: CalendarCheck, label: "Réservations", value: s?.reservations ?? 0 },
                { icon: Euro,          label: "Revenu généré", value: `${(s?.revenue ?? 0).toLocaleString("fr-FR")} €` },
                { icon: Euro,          label: "Commission perçue", value: `${(s?.commission ?? 0).toLocaleString("fr-FR")} €` },
                { icon: BedDouble,     label: "ADR", value: `${(s?.adr ?? 0).toLocaleString("fr-FR")} €` },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-[#f9fafb] border border-[#e5e7eb] rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                      <Icon size={12} />
                      <p className="text-[10px]">{item.label}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{item.value}</p>
                  </div>
                );
              })}
            </div>

            {/* Chambres */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">
                Chambres ({detail.rooms?.length || 0})
              </p>
              {detail.rooms?.length === 0 ? (
                <p className="text-xs text-gray-400">Aucune chambre configurée.</p>
              ) : (
                <div className="flex flex-col gap-1">
                  {detail.rooms.map(r => (
                    <div key={r._id} className="flex items-center gap-2 px-3 py-2 bg-[#f9fafb] rounded-lg text-xs text-gray-600">
                      <BedDouble size={12} className="text-gray-400" /> {r.title}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dernières réservations */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">Dernières réservations</p>
              {detail.recentReservations?.length === 0 ? (
                <p className="text-xs text-gray-400">Aucune réservation.</p>
              ) : (
                <div className="flex flex-col">
                  {detail.recentReservations.map(r => (
                    <div key={r._id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-700 truncate">{r.guest?.firstName} {r.guest?.lastName}</p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(r.checkInDate).toLocaleDateString("fr-FR")} → {new Date(r.checkOutDate).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <p className="text-xs font-medium text-gray-900 shrink-0">{(r.totalPrice || 0).toLocaleString("fr-FR")} €</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              {/* 🆕 Activer / Désactiver l'hôtel */}
              <button
                onClick={handleToggleActive}
                disabled={toggleLoading}
                className={`w-full py-2.5 text-sm font-medium rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-60 ${
                  h.active
                    ? "border border-red-200 text-red-600 hover:bg-red-50"
                    : "bg-[var(--k-green)] text-white hover:bg-[var(--k-green-hover)]"
                }`}
              >
                {toggleLoading ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
                {h.active ? "Désactiver l'hôtel" : "Activer l'hôtel"}
              </button>

              {/* Mode Vision */}
              <button
                onClick={handleVision}
                disabled={visionLoading}
                className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {visionLoading ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                Mode Vision — back-office
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function SuperAdminHotels() {
  const [tab, setTab] = useState("hotels"); // hotels | requests
  const [hotels, setHotels] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [islandFilter, setIslandFilter] = useState("all");
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/hotels`, { withCredentials: true }),
      axios.get(`${API}/hotel-requests`, { withCredentials: true }),
    ])
      .then(([h, r]) => { setHotels(h.data); setRequests(r.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleValidate = async (req) => {
    if (!window.confirm(`Valider "${req.hotelName}" ? Le compte hôtelier sera créé et l'email d'activation envoyé à ${req.email}.`)) return;
    setActionLoading(req._id);
    try {
      await axios.post(`${API}/hotel-requests/${req._id}/validate`, {}, { withCredentials: true });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la validation.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (req) => {
    if (!window.confirm(`Rejeter la demande "${req.hotelName}" ?`)) return;
    setActionLoading(req._id);
    try {
      await axios.post(`${API}/hotel-requests/${req._id}/reject`, {}, { withCredentials: true });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = requests.filter(r => r.status === "pending").length;

  const filtered = hotels
    .filter(h => islandFilter === "all" || h.island === islandFilter)
    .filter(h =>
      h.name.toLowerCase().includes(search.toLowerCase()) ||
      h.city.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Hôtels</h1>
          <p className="text-sm text-gray-400 mt-0.5">{hotels.length} établissements sur la plateforme</p>
        </div>
        <button
          onClick={fetchAll}
          className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── ONGLETS ── */}
      <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] rounded-xl p-1 w-fit">
        {[
          { key: "hotels", label: "Hôtels" },
          { key: "requests", label: `Demandes${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition duration-150 ${
              tab === t.key ? "bg-[var(--k-green)] text-white" : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "hotels" ? (
        <>
          {/* ── FILTRES ── */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un hôtel..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
              />
            </div>
            <select
              value={islandFilter}
              onChange={e => setIslandFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition text-gray-600"
            >
              <option value="all">Toutes les îles</option>
              <option value="Ngazidja">Ngazidja</option>
              <option value="Anjouan">Anjouan</option>
              <option value="Moheli">Mohéli</option>
            </select>
          </div>

          {/* ── TABLE HÔTELS ── */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e5e7eb] text-left">
                    {["Hôtel", "Île", "Chambres", "Résa", "Revenu", "Commission", "ADR", "Score qualité", "Statut"].map(h => (
                      <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-gray-400">Aucun hôtel trouvé.</td></tr>
                  ) : filtered.map(h => (
                    <tr
                      key={h._id}
                      onClick={() => setSelectedHotel(h._id)}
                      className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer transition duration-150"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 whitespace-nowrap">{h.name}</p>
                        <p className="text-[11px] text-gray-400">{h.city} · {TYPE_LABELS[h.type] || h.type}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ISLAND_LABELS[h.island] || h.island}</td>
                      <td className="px-4 py-3 text-gray-600">{h.roomsCount}</td>
                      <td className="px-4 py-3 text-gray-600">{h.reservationsCount}</td>
                      <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">{h.revenue.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-3 text-[var(--k-green)] font-medium whitespace-nowrap">{h.commission.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{h.adr.toLocaleString("fr-FR")} €</td>
                      <td className="px-4 py-3"><QualityBar score={h.qualityScore} /></td>
                      <td className="px-4 py-3"><StatusPill status={h.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        /* ── ONGLET DEMANDES ── */
        <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
          {loading ? (
            <p className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</p>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center py-16 gap-2">
              <Inbox className="text-gray-300" size={28} />
              <p className="text-xs text-gray-400">Aucune demande d'inscription pour le moment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e5e7eb] text-left">
                    {["Hôtel", "Île / Ville", "Contact", "Description", "Reçue le", "Statut", "Actions"].map(h => (
                      <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 whitespace-nowrap">{r.hotelName}</p>
                        <p className="text-[11px] text-gray-400">{TYPE_LABELS[r.type] || r.type}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ISLAND_LABELS[r.island] || r.island} · {r.city}</td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700 text-xs">{r.email}</p>
                        <p className="text-[11px] text-gray-400">{r.phone}</p>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <p className="text-xs text-gray-500 truncate">{r.description || "—"}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(r.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-4 py-3"><RequestStatusPill status={r.status} /></td>
                      <td className="px-4 py-3">
                        {r.status === "pending" ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleValidate(r)}
                              disabled={actionLoading === r._id}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[var(--k-green)] text-white text-xs font-medium hover:bg-[var(--k-green-hover)] transition disabled:opacity-50"
                            >
                              {actionLoading === r._id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                              Valider
                            </button>
                            <button
                              onClick={() => handleReject(r)}
                              disabled={actionLoading === r._id}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition disabled:opacity-50"
                            >
                              <XCircle size={12} />
                              Rejeter
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            {r.status === "validated" ? "Email d'activation envoyé" : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selectedHotel && (
        <HotelDrawer
          hotelId={selectedHotel}
          onClose={() => setSelectedHotel(null)}
          onToggled={fetchAll}
        />
      )}
    </div>
  );
}